/**
 * Fetch the authored header once, on the server, for every page.
 *
 * A plugin rather than leaving it to the consumers, for a reason specific to
 * this storefront's layout: the mobile drawer is mounted inside `<ClientOnly>`
 * (`layouts/default.vue`), so `useMobileNav()` never runs during SSR. Left to
 * the drawer alone the query would fire only after hydration — an extra round
 * trip on every page, and navigation absent from the server-rendered HTML.
 *
 * Registering it here starts the request during the plugin phase, before the
 * render begins, and puts the result in the payload. Both surfaces then read it
 * through `useHeaderMenus()`, whose `useAsyncData` key matches this one, so the
 * drawer and the desktop header share a single request rather than issuing one
 * each — see the `dedupe` / `getCachedData` notes there, which are what make
 * that true on the SERVER as well as during hydration.
 *
 * Mirrors `plugins/collections.ts`, which does the same for the collection list.
 */
export default defineNuxtPlugin((nuxtApp) => {
  // Nothing to prefetch behind an error page, and the header is not rendered
  // there either.
  if (nuxtApp.payload.error)
    return

  /*
   * Registering the query is the whole job — deliberately NOT `await
   * refresh()`.
   *
   * `useAsyncData` already starts the fetch on the server and registers it on
   * `app:created`, which Nuxt awaits before rendering (nuxt/dist/app/entry.js),
   * so the data is in the payload either way. `refresh()` on top of that is a
   * SECOND request: it bypasses the cache by design, and its `dedupe: 'cancel'`
   * default aborted the fetch this call had just started. On the client it is
   * pure waste — the payload is already there.
   */
  useHeaderMenus()
})
